import {
  Header,
  type IWithPopoverProps,
  Space,
  StatusLabel,
  StatusLabelStatuses,
  SubText,
  WithPopover,
  Card,
} from '@procurify/ui'

import { useFormatDate } from '@webapp/core/components/FormattedDate/useFormatDate'
import { useFormatMedia } from '@webapp/core/hooks/useFormatMedia'
import { type UserLike } from '@webapp/core/interfaces/components/UserLike'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { WithUserTooltipProfileImageStyled } from './styles/WithUserTooltipProfileImageStyled'
import { WithUserTooltipStyled } from './styles/WithUserTooltipStyled'

export interface IWithUserTooltipProps extends Partial<IWithPopoverProps> {
  user: UserLike
  disabled?: boolean
}

export const WithUserTooltipInfo = ({
  user,
}: {
  user: IWithUserTooltipProps['user']
}) => {
  const intl = useProcurifyIntl()
  const formatDate = useFormatDate()
  const formatMedia = useFormatMedia()

  return (
    <WithUserTooltipStyled>
      {user.profileImage && (
        <WithUserTooltipProfileImageStyled
          profileImage={formatMedia(user.profileImage) || ''}
        />
      )}
      <Card.Content p={1}>
        <Space size='small'>
          <Header as='h5'>{user.fullName}</Header>

          {user.primaryCategory && (
            <div>
              <SubText>
                <span>{user.primaryCategory.name}</span>
                {user.secondaryCategory && (
                  <>
                    <br />
                    <span>{user.secondaryCategory.name}</span>
                  </>
                )}
              </SubText>
            </div>
          )}

          {user.approvalDelegatee && (
            <StatusLabel
              fontSize={10}
              text={intl.formatMessage(messages.approvalsDelegatedLabel)}
              type={StatusLabelStatuses.WARNING}
            />
          )}

          {user.approvalDelegatee && (
            <SubText>
              <span>
                {intl.formatMessage(messages.delegatedTo, {
                  name: user.approvalDelegatee.fullName,
                })}
              </span>
              {user?.approvalDelegatee?.expectedReturnDate && (
                <>
                  <br />
                  <span>
                    {intl.formatMessage(messages.expectedToReturn, {
                      date: formatDate(
                        user.approvalDelegatee.expectedReturnDate
                      ),
                    })}
                  </span>
                </>
              )}
            </SubText>
          )}
        </Space>
      </Card.Content>
    </WithUserTooltipStyled>
  )
}

export const WithUserTooltip = ({
  children,
  trigger,
  placement,
  user,
  disabled,
}: IWithUserTooltipProps) => {
  return (
    <WithPopover
      content={
        <div>
          <WithUserTooltipInfo user={user} />
        </div>
      }
      placement={placement || 'top'}
      trigger={trigger || 'hover'}
      disableHoverListener={disabled}
      TriggerProps={{
        display: 'inline-flex',
      }}
    >
      {children}
    </WithPopover>
  )
}
